import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {FilterInput} from 'src/components/input';
import {SectionFilter} from 'src/components/section-filter';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {NoResultPage} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button, {FlatButton} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {StickyElement} from '@pb/kit-component-sticky-element';
import Typography from '@pb/kit-component-typography';
import {pluralize} from '@pb/kit-formatters';

import {ChangeLogTable} from './change-log-table';
import {SECTION_NAME} from './constants';
import {useGetEntitlementGrantActionTypeDictionary} from './hooks/useGetEntitlementGrantActionTypeDictionary';
import {useGetEntitlementGrantChangeLog} from './hooks/useGetEntitlementGrantChangeLog';
import {LogFiltersBody} from './log-filters-body';
import {entitlementGrantChangeLogStateActions} from './slices/entitlement-grant-change-log-state.slice';
import {entitlementGrantChangeLogActions} from './slices/entitlement-grant-change-log.slice';

import {
    editGrantChangeLogHeader,
    editGrantChangeLogHeaderCaption,
    editGrantChangeLogHeaderSticky,
    editGrantChangeLogLoadingContainer,
    editGrantChangeLogSeparator,
    editGrantChangeLogTotalCount,
} from './edit-grant-change-log.module.styl';

import type {EntitlementGrantLogsCriteria} from './typedef';


type Props = {
    grantId: string,
};

export const EntitlementGrantChangeLog = ({grantId}: Props) => {
    const dispatch = useDispatch();

    const {loadingError, result, cacheKeys} = useGetEntitlementGrantChangeLog({grantId});
    const {
        data: actionTypeDictionary,
        loadingError: actionTypeDictionaryLoadingError,
    } = useGetEntitlementGrantActionTypeDictionary();

    const noAccountResultMessage = useMemo(() => {
        if (Object.keys(cacheKeys.filters).length && !result?.data.length) {
            return (
                <NoResultPage
                    title="No data found for the selected criteria."
                    description=""
                />
            );
        } else if (!Object.keys(cacheKeys.filters).length) {
            return (
                <NoResultPage
                    title="No data to show."
                    description=""
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);

    const changeEntitlementName = useCallback((value: string) => {
        dispatch(entitlementGrantChangeLogStateActions.setFilters({
            ...cacheKeys.filters,
            entitlementName: value,
        }));
    }, [cacheKeys.filters, dispatch]);

    const clearEntitlementName = useCallback(() => {
        const {entitlementName, ...otherFilters} = cacheKeys.filters;
        dispatch(entitlementGrantChangeLogStateActions.setFilters(otherFilters));
    }, [cacheKeys.filters, dispatch]);

    const applyFilters = useCallback((filters: EntitlementGrantLogsCriteria) => {
        dispatch(entitlementGrantChangeLogStateActions.setFilters(filters));
    }, [dispatch]);

    const clearFilters = useCallback(() => {
        const {startDate, endDate, actionName, ...otherFilters} = cacheKeys.filters;

        dispatch(entitlementGrantChangeLogStateActions.setFilters(otherFilters));
        return otherFilters;
    }, [cacheKeys.filters, dispatch]);

    const clearAllFilters = useCallback(() => {
        dispatch(entitlementGrantChangeLogStateActions.setFilters({}));
    }, [dispatch]);

    useEffect(() => {
        return () => {
            dispatch(entitlementGrantChangeLogActions.clearData());
            dispatch(entitlementGrantChangeLogStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <ServerResponseHandler
            serverError={loadingError || actionTypeDictionaryLoadingError}
            sectionName={SECTION_NAME}>
            <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                <StickyElement className={editGrantChangeLogHeaderSticky}>
                    <div className={editGrantChangeLogHeader}>
                        <header className={editGrantChangeLogHeaderCaption}>
                            <Group direction="h" alignItems="center">
                                <Typography fontWeight="semi-bold" variant="heading-level-3">Change Log</Typography>
                                {result?.totalCount ? (
                                    <>
                                        <span className={editGrantChangeLogSeparator}/>
                                        <span className={editGrantChangeLogTotalCount}>
                                            {pluralize(`${result.totalCount} Action[s]`, result.totalCount)}
                                        </span>
                                    </>
                                ) : null}
                            </Group>
                        </header>
                        {!(!result?.totalCount && !Object.keys(cacheKeys.filters).length) ? (
                            <Group direction="h" gap="m" as="section">
                                <FilterInput
                                    id="entitlement-grant-change-log-filter"
                                    placeholder="Search for actions by description"
                                    onChange={changeEntitlementName}
                                    onClear={clearEntitlementName}
                                    timeOut={1000}
                                    width={600}
                                    value={(cacheKeys.filters?.entitlementName || '') as string}
                                />
                                <SectionFilter<EntitlementGrantLogsCriteria>
                                    onApply={applyFilters}
                                    onClear={clearFilters}
                                    filters={cacheKeys.filters as EntitlementGrantLogsCriteria}
                                    getAnchor={({anchorRef, positionRef, getAnchorProps}) => {
                                        const count = Object
                                            .keys(cacheKeys.filters as EntitlementGrantLogsCriteria)
                                            .filter(key => key !== 'entitlementName')
                                            .filter(Boolean).length;

                                        const filterIconType = count ? Icons.FILTER_FILLED : Icons.FILTER_EMPTY;

                                        return (
                                            <Button
                                                variant="Type.SECONDARY"
                                                startIcon={<Icon type={filterIconType}/>}
                                                endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                                                ref={anchorRef}
                                                {...getAnchorProps()}>
                                                <b>Action Type & Date</b>
                                            </Button>
                                        );
                                    }}
                                    defaultFilters={{}}>
                                    {(filter, onChange) => (
                                        <LogFiltersBody
                                            filter={filter}
                                            onChange={onChange}
                                            actionTypeDictionary={actionTypeDictionary}
                                        />
                                    )}
                                </SectionFilter>
                                <FlatButton onClick={clearAllFilters}>
                                    <b>Clear all filters</b>
                                </FlatButton>
                            </Group>
                        ) : null}
                    </div>
                </StickyElement>
                <BoxLoading loading={!result}>
                    {result?.data.length ? (
                        <div className={editGrantChangeLogLoadingContainer}>
                            <ChangeLogTable
                                rows={result?.data || []}
                                totalCount={result?.totalCount || 0}
                                pagination={cacheKeys.pagination}
                                actionTypeDictionary={actionTypeDictionary}
                            />
                        </div>
                    ) : noAccountResultMessage}
                </BoxLoading>
            </ErrorBoundary>
        </ServerResponseHandler>
    );
};
